import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  LinearProgress,
  MenuItem,
  OutlinedInput,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LinkIcon from '@mui/icons-material/Link';
import { useParams } from 'react-router-dom';
import { COVERAGE_TAG_COLORS, SAVE_PHASE } from '../../test-cycles/static-data';
import MatchingTestCases, { AutomationCandidateBadge, CrossMatchBadge } from './MatchingTestCases';

const CoverageTag = ({ tag }) => {
  const style = COVERAGE_TAG_COLORS[tag] || { bg: '#F3F4F6', color: '#374151' };
  return (
    <Box
      sx={{
        display: 'inline-block',
        px: 1,
        py: 0.25,
        borderRadius: '10px',
        bgcolor: style.bg,
        color: style.color,
        fontSize: '10px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        flexShrink: 0,
      }}
    >
      {tag}
    </Box>
  );
};

const ExpandableText = ({ label, text }) => {
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const el = textRef.current;
    if (el) setIsClamped(el.scrollHeight > el.clientHeight);
  }, [text]);

  if (!text) return null;

  return (
    <Box sx={{ mt: 0.75 }}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: 'text.secondary',
          textTransform: 'uppercase',
          fontSize: '10px',
          letterSpacing: '0.4px',
        }}
      >
        {label}
      </Typography>
      <Typography
        ref={textRef}
        variant="caption"
        color="text.secondary"
        component="p"
        sx={{
          mt: 0.25,
          lineHeight: 1.5,
          margin: 0,
          ...(!expanded && {
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }),
        }}
      >
        {text}
      </Typography>
      {(isClamped || expanded) && (
        <Button
          size="small"
          onClick={() => setExpanded((v) => !v)}
          sx={{ fontSize: '11px', p: 0, minWidth: 0, textTransform: 'none', mt: 0.25 }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </Box>
  );
};

const SaveProgressOverlay = ({ savePhase, saveProgress }) => {
  const isSaving = savePhase === SAVE_PHASE.SAVING;
  const isLinking = savePhase === SAVE_PHASE.LINKING;

  const pct = saveProgress && saveProgress.total > 0
    ? Math.round((saveProgress.current / saveProgress.total) * 100)
    : 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 4,
        gap: 3,
        minHeight: 260,
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          bgcolor: 'primary.50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px solid',
          borderColor: 'primary.200',
        }}
      >
        {isLinking
          ? <LinkIcon sx={{ fontSize: 28, color: 'primary.main' }} />
          : <CircularProgress size={28} thickness={3} />
        }
      </Box>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
          {isLinking ? 'Linking to Jira issue' : 'Saving test cases…'}
        </Typography>
        {isSaving && saveProgress && (
          <Typography variant="body2" color="text.secondary">
            {saveProgress.current} of {saveProgress.total}
            {saveProgress.summary ? ` — ${saveProgress.summary}` : ''}
          </Typography>
        )}
        {isLinking && (
          <Typography variant="body2" color="text.secondary">
            Creating traceability links in Jira
          </Typography>
        )}
      </Box>

      {isSaving && saveProgress && saveProgress.total > 0 && (
        <Box sx={{ width: '100%', maxWidth: 320 }}>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{ height: 6, borderRadius: 3 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}
          >
            {pct}%
          </Typography>
        </Box>
      )}

      {isLinking && (
        <Box sx={{ width: '100%', maxWidth: 320 }}>
          <LinearProgress sx={{ height: 6, borderRadius: 3 }} />
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
        Please don't close this window
      </Typography>
    </Box>
  );
};

const SaveResultBanner = ({ savePhase, saveResult }) => {
  if (
    savePhase !== SAVE_PHASE.SUCCESS &&
    savePhase !== SAVE_PHASE.PARTIAL &&
    savePhase !== SAVE_PHASE.ERROR
  ) return null;

  const isSuccess = savePhase === SAVE_PHASE.SUCCESS;
  const isPartial = savePhase === SAVE_PHASE.PARTIAL;
  const isError = savePhase === SAVE_PHASE.ERROR;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 2,
        mb: 2,
        borderRadius: 1.5,
        border: '1.5px solid',
        borderColor: isSuccess ? 'success.light' : isPartial ? 'warning.light' : 'error.light',
        bgcolor: isSuccess ? '#F0FDF4' : isPartial ? '#FFFBEB' : '#FEF2F2',
      }}
    >
      {isSuccess && (
        <CheckCircleIcon sx={{ fontSize: 22, color: 'success.main', flexShrink: 0, mt: 0.1 }} />
      )}
      {isPartial && (
        <WarningAmberIcon sx={{ fontSize: 22, color: 'warning.main', flexShrink: 0, mt: 0.1 }} />
      )}
      {isError && (
        <ErrorOutlineIcon sx={{ fontSize: 22, color: 'error.main', flexShrink: 0, mt: 0.1 }} />
      )}

      <Box>
        <Typography variant="body2" fontWeight={600} color={
          isSuccess ? 'success.dark' : isPartial ? 'warning.dark' : 'error.dark'
        }>
          {isSuccess && `${saveResult.successCount} test case${saveResult.successCount !== 1 ? 's' : ''} created successfully`}
          {isPartial && `${saveResult.successCount} saved, ${saveResult.failedCount} failed`}
          {isError && 'All test cases failed to save'}
        </Typography>

        {isSuccess && saveResult.storyKey && !saveResult.linkError && (
          <Typography variant="caption" color="success.dark" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
            <LinkIcon sx={{ fontSize: 13 }} />
            Linked to {saveResult.storyKey} · Closing in a moment…
          </Typography>
        )}
        {isSuccess && saveResult.linkError && (
          <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 0.25 }}>
            Note: Jira linking failed. Link manually from the traceability tab.
          </Typography>
        )}

        {isPartial && (
          <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 0.25 }}>
            Failed cards are highlighted below. Use Retry Failed to attempt them again.
            {saveResult.linkError && ' Jira linking also failed for saved test cases.'}
          </Typography>
        )}
        {isError && (
          <Typography variant="caption" color="error.dark" sx={{ display: 'block', mt: 0.25 }}>
            Please check your connection and try again.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const JiraReviewScreen = ({
  tcs,
  onTcsChange,
  technologyLayers,
  modules = [],
  labels = [],
  onEdit,
  savePhase = SAVE_PHASE.IDLE,
  saveProgress = null,
  saveResult = null,
  existingMatches = [],
  selectedExisting,
  onExistingToggle,
  selectedStory = null,
  tokenUsage = null,
}) => {
  const { projectId } = useParams();
  const isBulkOperationActive = savePhase === SAVE_PHASE.SAVING || savePhase === SAVE_PHASE.LINKING;
  const isSuccess = savePhase === SAVE_PHASE.SUCCESS;
  const submitting = isBulkOperationActive || isSuccess;
  const isStoryFlow = !!selectedStory;
  const selectedCount = tcs.filter((tc) => tc.selected).length;
  const selectedWithNoTechLayer = tcs.filter((tc) => tc.selected && !tc.technologyLayer);
  const allSelected = tcs.length > 0 && tcs.every((tc) => tc.selected);
  const someSelected = tcs.some((tc) => tc.selected) && !allSelected;

  const toggleSelectAll = () =>
    onTcsChange(tcs.map((tc) => ({ ...tc, selected: !allSelected })));
  const toggleSelect = (id) =>
    onTcsChange(tcs.map((tc) => (tc._id === id ? { ...tc, selected: !tc.selected } : tc)));
  const toggleSteps = (id) =>
    onTcsChange(tcs.map((tc) => (tc._id === id ? { ...tc, stepsExpanded: !tc.stepsExpanded } : tc)));
  const handleTechLayerChange = (id, value) =>
    onTcsChange(tcs.map((tc) => (tc._id === id ? { ...tc, technologyLayer: value } : tc)));

  if (tcs.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No test cases were generated. Please go back and try again.
        </Typography>
      </Box>
    );
  }

  if (isBulkOperationActive) {
    return (
      <SaveProgressOverlay savePhase={savePhase} saveProgress={saveProgress} />
    );
  }

  if (isSuccess) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          gap: 2,
          minHeight: 260,
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: '#F0FDF4',
            border: '2px solid',
            borderColor: 'success.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 36, color: 'success.main' }} />
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="subtitle1" fontWeight={600} color="success.dark">
            {saveResult?.successCount} test case{saveResult?.successCount !== 1 ? 's' : ''} created
          </Typography>
          {saveResult?.storyKey && !saveResult?.linkError && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              <LinkIcon sx={{ fontSize: 15 }} />
              Linked to {saveResult.storyKey}
            </Typography>
          )}
          {saveResult?.linkError && (
            <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 0.5 }}>
              Jira linking failed — link manually from traceability tab
            </Typography>
          )}
        </Box>
        <CircularProgress size={16} thickness={3} sx={{ color: 'success.main', mt: 1 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 1.5, justifyContent: 'space-between', alignItems: 'center' }}>

        {/* Left — token usage */}
        {tokenUsage ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.5, mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Tokens used:
            </Typography>
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              {tokenUsage.total_tokens.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              ({tokenUsage.prompt_tokens.toLocaleString()} input · {tokenUsage.output_tokens.toLocaleString()} output)
            </Typography>
          </Box>
        ) : <Box />}

        {/* Right — existing legend */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4338ca' }} />
            <Typography variant="caption" color="text.secondary">Module</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#165c33' }} />
            <Typography variant="caption" color="text.secondary">Label</Typography>
          </Box>
        </Box>

      </Box>
      <SaveResultBanner savePhase={savePhase} saveResult={saveResult} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          py: 1,
          mb: 1,
          bgcolor: '#f9fafb',
          borderRadius: 1,
          border: '1px solid #e5e7eb',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={someSelected}
            onChange={toggleSelectAll}
            disabled={submitting}
            inputProps={{ 'aria-label': 'Select all test cases' }}
          />
          <Typography variant="body2" color="text.secondary">Select All</Typography>
          <Chip
            label={`${selectedCount} selected`}
            size="small"
            color="primary"
            sx={{ fontWeight: 600, fontSize: '11px' }}
          />
          <Typography variant="caption" color="text.secondary">of {tcs.length}</Typography>
        </Box>

        {selectedWithNoTechLayer.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <WarningAmberIcon sx={{ fontSize: 14, color: 'warning.main' }} />
            <Typography variant="caption" color="warning.dark" fontWeight={500}>
              {selectedWithNoTechLayer.length} selected{' '}
              {selectedWithNoTechLayer.length === 1 ? 'requires' : 'require'} tech layer
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pr: 0.5 }}>
        {tcs.map((tc) => {
          const missingTechLayer = tc.selected && !tc.technologyLayer;
          const hasSaveError = !!tc.saveError;

          const moduleName = tc.module
            ? (modules.find((m) => m.id === tc.module)?.name ?? null)
            : null;

          const resolvedLabels = Array.isArray(tc.labels)
            ? tc.labels.map((id) => labels.find((l) => l.id === id)).filter(Boolean)
            : [];

          return (
            <Box
              key={tc._id}
              sx={{
                border: '1.5px solid',
                borderColor: hasSaveError
                  ? 'error.main'
                  : missingTechLayer
                    ? 'warning.main'
                    : tc.selected
                      ? 'primary.main'
                      : '#e5e7eb',
                borderRadius: 1.5,
                overflow: 'hidden',
                transition: 'border-color 0.15s, opacity 0.15s',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1.5, p: 1.5, alignItems: 'flex-start' }}>
                <Checkbox
                  size="small"
                  checked={tc.selected}
                  onChange={() => toggleSelect(tc._id)}
                  disabled={submitting}
                  inputProps={{ 'aria-label': `Select: ${tc.summary}` }}
                  sx={{ mt: 0.25, flexShrink: 0 }}
                />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <CoverageTag tag={tc.coverageTag} />
                      {tc?.storyKey && (
                        <Tooltip
                          title={tc.storyTitle}
                          arrow
                          placement="top"
                        >
                          <Chip
                            size="small"
                            label={tc.storyKey}
                            sx={{
                              height: 20,
                              fontSize: '11px',
                              fontWeight: 700,
                              bgcolor: '#EEF2FF',
                              color: '#4338CA',
                              border: '1px solid #C7D2FE',
                              cursor: 'pointer',
                              flexShrink: 0,
                            }}
                          />
                        </Tooltip>
                      )}</Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <CrossMatchBadge
                      matchKey={tc._crossMatchKey}
                      score={tc._crossMatchScore}
                      strength={tc._crossMatchStrength}
                    />
                      <AutomationCandidateBadge
                        candidate={tc._automationCandidate}
                      />
                    </Box>

                    {hasSaveError && (
                      <Tooltip title={tc.saveError} placement="top">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            bgcolor: '#FEE2E2',
                            border: '1px solid #FCA5A5',
                            borderRadius: '10px',
                            px: 1,
                            py: 0.25,
                            flexShrink: 0,
                            cursor: 'default',
                          }}
                        >
                          <ErrorOutlineIcon sx={{ fontSize: 11, color: 'error.dark' }} />
                          <Typography variant="caption" color="error.dark" fontWeight={600} sx={{ fontSize: '11px' }}>
                            Save failed
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}

                    {!hasSaveError && missingTechLayer && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          bgcolor: '#FEF3C7',
                          border: '1px solid #FCD34D',
                          borderRadius: '10px',
                          px: 1,
                          py: 0.25,
                          flexShrink: 0,
                        }}
                      >
                        <WarningAmberIcon sx={{ fontSize: 11, color: 'warning.dark' }} />
                        <Typography variant="caption" color="warning.dark" fontWeight={600} sx={{ fontSize: '11px' }}>
                          Tech layer required
                        </Typography>
                      </Box>
                    )}

                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon sx={{ fontSize: '12px !important' }} />}
                      onClick={() => onEdit(tc)}
                      disabled={submitting}
                      aria-label={`Edit: ${tc.summary}`}
                      sx={{
                        fontSize: '12px',
                        py: 0.25,
                        px: 1,
                        minWidth: 0,
                        borderColor: '#BFDBFE',
                        color: 'primary.main',
                        '&:hover': { bgcolor: '#EFF6FF', borderColor: '#93C5FD' },
                      }}
                    >
                      Edit
                    </Button>
                  </Box>
                  </Box>
                  <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>
                    {tc.summary}
                  </Typography>

                  <ExpandableText label="Objective" text={tc.objective} />
                  <ExpandableText label="Pre-condition" text={tc.preCondition} />

                  {(moduleName || resolvedLabels.length > 0) && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      {moduleName && (
                        <Chip
                          label={moduleName}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontSize: '11px',
                            height: 20,
                            borderColor: '#c7d2fe',
                            color: '#4338ca',
                            bgcolor: '#eef2ff',
                          }}
                        />
                      )}
                      {resolvedLabels.map((lbl) => (
                        <Chip
                          key={lbl.id}
                          label={lbl.name}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontSize: '11px',
                            height: 20,
                            borderColor: '#3d9260',
                            color: '#165c33',
                            bgcolor: 'rgb(243, 245, 243)',
                          }}
                        />
                      ))}
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Tech Layer
                    </Typography>
                    <FormControl size="small">
                      <Select
                        value={tc.technologyLayer || ''}
                        onChange={(e) => handleTechLayerChange(tc._id, e.target.value)}
                        input={<OutlinedInput />}
                        IconComponent={KeyboardArrowDownIcon}
                        displayEmpty
                        disabled={submitting}
                        inputProps={{ 'aria-label': `Tech layer for: ${tc.summary}` }}
                        renderValue={(val) =>
                          val
                            ? (technologyLayers.find((t) => t.id === val)?.name ?? val)
                            : <span style={{ color: '#9e9e9e' }}>Select</span>
                        }
                        sx={{
                          fontSize: '12px',
                          height: '28px',
                          minWidth: 130,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: missingTechLayer ? '#FCD34D' : '#ced4da',
                          },
                          bgcolor: missingTechLayer ? '#FFFBEB' : 'transparent',
                        }}
                      >
                        <MenuItem value="" disabled>
                          <span style={{ color: '#9e9e9e', fontSize: '12px' }}>Select tech layer</span>
                        </MenuItem>
                        {technologyLayers?.map((tl) => (
                          <MenuItem key={tl.id} value={tl.id} sx={{ fontSize: '12px' }}>
                            {tl.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </Box>

              {tc.testSteps?.length > 0 && (
                <>
                  <Button
                    fullWidth
                    size="small"
                    onClick={() => toggleSteps(tc._id)}
                    disabled={submitting}
                    endIcon={tc.stepsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    aria-expanded={tc.stepsExpanded}
                    sx={{
                      borderTop: '1px solid #f3f4f6',
                      borderRadius: 0,
                      justifyContent: 'flex-start',
                      pl: 5.5,
                      py: 0.75,
                      fontSize: '12px',
                      color: 'primary.main',
                      fontWeight: 600,
                      bgcolor: 'transparent',
                      '&:hover': { bgcolor: '#f9fafb' },
                    }}
                  >
                    {tc.stepsExpanded ? 'Hide' : 'View'} Steps ({tc.testSteps.length})
                  </Button>

                  <Collapse in={tc.stepsExpanded}>
                    <Box sx={{ borderTop: '1px solid #f3f4f6', bgcolor: '#fafafa', px: 2, py: 1 }}>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '24px 1fr 1fr 1fr',
                          gap: 1,
                          pb: 0.5,
                          mb: 0.5,
                          borderBottom: '1px solid #e5e7eb',
                        }}
                      >
                        {['', 'Description', 'Test Data', 'Expected Result'].map((h) => (
                          <Typography
                            key={h}
                            variant="caption"
                            sx={{
                              fontSize: '10px',
                              fontWeight: 700,
                              color: '#9ca3af',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}
                          >
                            {h}
                          </Typography>
                        ))}
                      </Box>

                      {tc.testSteps.map((step, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '24px 1fr 1fr 1fr',
                            gap: 1,
                            py: 0.75,
                            borderBottom:
                              idx < tc.testSteps.length - 1
                                ? '1px solid #f0f0f0'
                                : 'none',
                            alignItems: 'start',
                          }}
                        >
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px',
                              fontWeight: 700,
                              flexShrink: 0,
                            }}
                          >
                            {idx + 1}
                          </Box>
                          <Typography variant="caption" color="text.primary" sx={{ lineHeight: 1.5 }}>
                            {step.step || '—'}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ lineHeight: 1.5, fontStyle: step.testData ? 'normal' : 'italic' }}
                          >
                            {step.testData || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.primary" sx={{ lineHeight: 1.5 }}>
                            {step.expectedResult || '—'}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                </>
              )}
            </Box>
          );
        })}
      </Box>
      <MatchingTestCases
        existingMatches={existingMatches}
        selectedIds={new Set(selectedExisting)}
        onToggle={onExistingToggle}
        disabled={submitting}
        projectId={projectId}
        linkable={isStoryFlow}
      />
    </Box>
  );
};

export default JiraReviewScreen;